/**
 * #578 — verify renderer delivery before QueueController skip-on-playUri-failure.
 *
 * Read-only: queries get_transport_info / last observed URI. Never dispatches Play
 * (#568 bare-Play retry may be in flight). Connector invoke budget is unchanged;
 * the skip verdict waits for this verify instead of treating a late invoke error
 * as non-delivery.
 */
import { diagnosticLog } from '../utils/diagnosticLogger';
import { uriSameTrack } from './uriSameTrack';
import {
  getPlayUriPendingSession,
  hasPlayUriAcceptanceEvidence,
} from './playUriConfirmation';
import {
  hasStartConfirmPositionProgress,
  isStartConfirmGraceActive,
  skipConfirmedFailureBeaconPayload,
} from './playUriStartConfirmWindow';
import {
  parseDidlTitleFromXml,
  parseTrackUriFromXml,
  parseTransportStateFromXml,
} from './playUriTransportXml';

export const FAILURE_SKIP_CAP = 3;

export type PlayUriFailureVerdict =
  | 'late_success'
  | 'confirmed_failure'
  | 'inconclusive';

export type ObservedRendererTrack = {
  rendererId: string;
  uri?: string | null;
  title?: string | null;
  artist?: string | null;
  transportState?: string | null;
};

export type DispatchedTrackIdentity = {
  id?: string | null;
  title?: string | null;
  artist?: string | null;
};

export type PlayUriFailureVerifyInput = {
  rendererId?: string | null;
  rendererName?: string | null;
  dispatchedUri?: string | null;
  dispatchedTrack?: DispatchedTrackIdentity | null;
  failureReason?: string | null;
  getTransportInfo: (rendererId: string) => Promise<string>;
  /** Injected last position-event observation (tests / live cache). */
  lastObserved?: ObservedRendererTrack | null;
};

const _lastObservedByRenderer = new Map<string, ObservedRendererTrack>();

export function noteObservedRendererTrack(obs: ObservedRendererTrack): void {
  if (!obs?.rendererId) return;
  _lastObservedByRenderer.set(obs.rendererId, { ...obs });
}

export function getLastObservedRendererTrack(rendererId?: string | null): ObservedRendererTrack | null {
  if (!rendererId) return null;
  return _lastObservedByRenderer.get(rendererId) ?? null;
}

export function resetPlayUriFailureVerifyForTests(): void {
  _lastObservedByRenderer.clear();
}

function normalizeTitle(value?: string | null): string {
  return String(value || '').trim().toLowerCase();
}

function titlesMatch(a?: string | null, b?: string | null): boolean {
  const left = normalizeTitle(a);
  const right = normalizeTitle(b);
  return !!left && !!right && left === right;
}

function isActiveTransportState(state: string): boolean {
  return state === 'PLAYING' || state === 'TRANSITIONING';
}

function isIdleTransportState(state: string): boolean {
  return state === 'STOPPED' || state === 'NO_MEDIA_PRESENT';
}

/**
 * Decide whether a playUri failure is a late success (renderer already playing
 * the dispatched track) or confirmed non-delivery. Skip only on confirmed.
 *
 * Never calls Play / playUri / resume.
 */
export async function verifyPlayUriFailureDelivery(
  input: PlayUriFailureVerifyInput,
): Promise<PlayUriFailureVerdict> {
  const rendererId = input.rendererId || '';
  const renderer = input.rendererName || rendererId || 'unknown';
  const reason = String(input.failureReason || 'play_uri_failed');

  if (!rendererId) {
    emitConfirmedFailure(reason, renderer);
    return 'confirmed_failure';
  }

  let raw = '';
  try {
    raw = await input.getTransportInfo(rendererId);
  } catch {
    return 'inconclusive';
  }

  const state = parseTransportStateFromXml(raw);
  const xmlUri = parseTrackUriFromXml(raw);
  const xmlTitle = parseDidlTitleFromXml(raw);
  const observed = input.lastObserved || getLastObservedRendererTrack(rendererId);
  const observedUri = xmlUri || observed?.uri || null;
  const observedTitle = xmlTitle || observed?.title || null;
  const dispatchedUri = input.dispatchedUri || null;
  const dispatchedTitle = input.dispatchedTrack?.title || null;

  if (isIdleTransportState(state)) {
    const session = getPlayUriPendingSession();
    if (isStartConfirmGraceActive(session, hasPlayUriAcceptanceEvidence(session))) {
      return 'inconclusive';
    }
    if (hasStartConfirmPositionProgress(session)) {
      emitLateSuccess(renderer);
      return 'late_success';
    }
    emitConfirmedFailure(reason, renderer, session);
    return 'confirmed_failure';
  }

  if (isActiveTransportState(state)) {
    const uriKnown = !!(observedUri && dispatchedUri);
    if (uriKnown && uriSameTrack(observedUri, dispatchedUri)) {
      emitLateSuccess(renderer);
      return 'late_success';
    }
    if (uriKnown && !uriSameTrack(observedUri, dispatchedUri)) {
      emitConfirmedFailure('renderer_playing_different_track', renderer);
      return 'confirmed_failure';
    }
    if (titlesMatch(observedTitle, dispatchedTitle)) {
      emitLateSuccess(renderer);
      return 'late_success';
    }
    if (observedTitle && dispatchedTitle && !titlesMatch(observedTitle, dispatchedTitle)) {
      emitConfirmedFailure('renderer_playing_different_track', renderer);
      return 'confirmed_failure';
    }
    // playUri reached the renderer and it is PLAYING — cannot prove a different
    // track (Cliff: SOAP already succeeded; URI not yet in GetTransportInfo).
    if (dispatchedUri) {
      emitLateSuccess(renderer);
      return 'late_success';
    }
    emitConfirmedFailure(reason, renderer);
    return 'confirmed_failure';
  }

  return 'inconclusive';
}

function emitLateSuccess(renderer: string): void {
  diagnosticLog('info', 'playback', 'skip_averted_late_success', { renderer });
}

function emitConfirmedFailure(
  reason: string,
  renderer: string,
  session = getPlayUriPendingSession(),
): void {
  diagnosticLog(
    'info',
    'playback',
    'skip_confirmed_failure',
    skipConfirmedFailureBeaconPayload(reason, renderer, session),
  );
}
